import {
  assertSuccessfulOperationResponse,
  attachmentFromBase64,
  ErrorReportAttachment,
  ErrorReportOperation,
} from '@imagise/entities';
import { map, mergeMap, MonoTypeOperatorFunction, Observable, of } from 'rxjs';

/** Freeze the actual image request; image bytes stay out of diagnostic text. */
export function captureOperationRequest(
  operation: string,
  endpoint: string,
  request: object,
  imageNames: readonly string[] = [],
): ErrorReportOperation {
  const files: ErrorReportAttachment[] = [];
  let imageIndex = 0;
  const withoutImages = (value: object): Record<string, unknown> => {
    const parameters: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      if (/^(imageBase64Files|imageBase64|imageFileAsBase64)$/i.test(key)) {
        const images = Array.isArray(item) ? item : [item];
        const references = images.map(image => {
          if (typeof image !== 'string') return image;
          const name = imageNames[imageIndex] ?? `input-${imageIndex + 1}.bin`;
          imageIndex += 1;
          const attachment = attachmentFromBase64(image, name);
          if (attachment) files.push({ ...attachment, inputIndex: imageIndex });
          return attachment ? `[attached input ${imageIndex}]` : `[input ${imageIndex} unavailable]`;
        });
        parameters[key] = Array.isArray(item) ? references : references[0];
      } else if (/^edits$/i.test(key) && Array.isArray(item)) {
        parameters[key] = item.map(edit => edit && typeof edit === 'object' ? withoutImages(edit) : edit);
      } else {
        parameters[key] = item;
      }
    }
    return parameters;
  };
  const parameters = { request: JSON.stringify(withoutImages(request)) };
  return { operation, endpoint, method: 'POST', parameters, files };
}

/** Generated image endpoints return Blob even for JSON failure envelopes. */
export function checkOperationResponse<T>(): MonoTypeOperatorFunction<T> {
  return mergeMap(response => {
    if (!(response instanceof Blob) || !/json/i.test(response.type)) {
      assertSuccessfulOperationResponse(response);
      return of(response);
    }
    // Diagnostic JSON is bounded; never read a large image into a text buffer.
    if (response.size > 1024 * 1024) throw new Error('Unexpected oversized JSON response.');
    return new Observable<string>(subscriber => {
      const reader = new FileReader();
      reader.onload = () => { subscriber.next(String(reader.result)); subscriber.complete(); };
      reader.onerror = () => subscriber.error(reader.error ?? new Error('Cannot read operation response.'));
      reader.readAsText(response);
      return () => { if (reader.readyState === FileReader.LOADING) reader.abort(); };
    }).pipe(map(text => {
      assertSuccessfulOperationResponse(JSON.parse(text));
      return response;
    }));
  });
}
